import React, {Fragment, FunctionComponent, useEffect, useState} from 'react'
import {DayPicker} from '@App/Components/DayPicker/DayPicker'
import {Slot} from '@App/types'
import {PremiumServiceSelector} from "@App/Components/PremiumServiceSelector"
import {useDates} from "@App/hooks/useDates"
import {TimeSlotPicker} from "@App/Components/TimeSlotPicker"
import {chooseSlot} from "@App/state/action-creators"
import {useDispatch, useSelector} from "react-redux"
import {actionCreators, State} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit"
import {GarageListItemPrice} from "@App/Components/GarageListItemPrice";
import {useConfig} from "@App/hooks/useConfig";
import {CheckpointDetails} from "@App/Components/CheckpointDetails";
import {useBlockSlot} from "@App/hooks/api/mutations/useBlockSlot";
import {useTranslation} from "react-i18next";
import {useGarageCalculation} from "@App/hooks/api/useGarageCalculation";
import {parseISO, startOfDay} from "date-fns";
import {useUnblockSlotOnMount} from "@App/hooks/useUnblockSlotOnMount";
import {Alert} from "@App/Components/common/Alert";
import {usePremiumServices} from "@App/hooks/api/usePremiumServices";
import {ServiceIsVehicleReturn} from "@App/utils/conditionals/services";
import {BookingHeadline} from "@App/Components/Partials/BookingHeadline";
import {SelectedHint} from "@App/Components/Partials/SelectedHint";
import {OrganizationIsAyvens} from "@App/utils/conditionals/organization";

type BookingProps = {
    onSubmit: (slot: Slot) => void
}

export const Booking: FunctionComponent<BookingProps> = ({onSubmit}: BookingProps) => {
    // hooks
    const {t} = useTranslation() // i18next
    const {formatDate} = useDates()
    const {garageCalculation} = useGarageCalculation() // graphql api hook
    // unblock a blocked slot, if the user already clicked and blocked a
    // slot in this session
    useUnblockSlotOnMount()
    // block slot graphql api hook for slot reservation
    const {
        triedSlot,
        blockSlot,
        error: errorWhenBlockingSlot,
        loading: loadingWhenBlockingSlot,
        blockedSlot
    } = useBlockSlot()

    // redux selectors
    const {displayPrices, displayPremiumServices} = useConfig()
    const service = useSelector((state: State) => state.orders.service)
    const garage = useSelector((state: State) => state.orders.garage)
    const selectedSlot = useSelector((state: State) => state.orders.slot)
    const organization = useSelector((state: State) => state.orders.organization)
    const place = useSelector((state: State) => state.orders.place)
    const brand = useSelector((state: State) => state.orders.brand)

    // redux action creators
    const dispatch = useDispatch();
    const {chooseSlot} = bindActionCreators(actionCreators, dispatch)

    // component state
    const [nextSlot, setNextSlot] = useState(null)
    const [initiallySelectedDay, setInitiallySelectedDay] = useState(null)

    const [selectedDay, setSelectedDay] = useState(selectedSlot ? startOfDay(parseISO(selectedSlot.starts_at)) : null)
    const [daysAvailable, setDaysAvailable] = useState(true)
    const [daysLoading, setDaysLoading] = useState(false)

    // business logic
    const changeDay = (day: Date) => {
        if (!initiallySelectedDay) {
            setInitiallySelectedDay(day)
        }
        setSelectedDay(day)
    }

    const changeSlot = (slot: Slot) => {
        chooseSlot(slot)
    }

    const changeEarliestSlot = (slot: Slot) => {
        setNextSlot(slot)
    }

    const selectFirstSlot = () => {
        setSelectedDay(startOfDay(parseISO(nextSlot.starts_at)))
        chooseSlot(nextSlot)
    }

    const {premiumServices: availablePremiumServices} = usePremiumServices()

    const premiumServices = useSelector((state: State) => state.orders.premiumServices)

    const noSubServiceSelected = () => {
        //we need to disable this for ayvens specifically because they dont need a subservice selected...
        if (OrganizationIsAyvens(organization)) {
            return false;
        }
        if(availablePremiumServices?.filter(premiumService => premiumService.service_type === 'sub-service').length > 0){
            return  premiumServices.filter(premiumService => premiumService.service_type === 'sub-service').length == 0;
        }
        return false;
    }

    const submit = () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'choose date',
            'garage': garage,
            'service': service,
            'location': place,
            'slot': selectedSlot.starts_at,
            'organization': organization.id
        });
        blockSlot(selectedSlot)
    }

    useEffect(() => {
        if (blockedSlot && blockedSlot.id) {
            chooseSlot(blockedSlot)
            onSubmit && onSubmit(selectedSlot)
        }
    }, [blockedSlot])

    return (
        <div
            className="md:container mx-auto mt-10 grid md:grid-cols-2 gap-x-16 justify-between border border-neutral-100 bg-neutral-100 rounded md:shadow p-8">
            <BookingHeadline service={service} organization={organization} brand={brand}/>

            <div className="my-5 md:my-0 row-span-5">
                <Alert display={!daysAvailable && !daysLoading} title={t('booking.no-slots')}/>
                {nextSlot && garage && garage.available_days.length > 0 && selectedDay == initiallySelectedDay && (
                    <Alert
                        display={startOfDay(parseISO(nextSlot.starts_at)) > startOfDay(parseISO(garage.available_days[0].available_at))}
                        title={t('booking.error-nearest-slot-gone', {
                            day: formatDate(garage.available_days[0].available_at, 'dd.'),
                            newDay: formatDate(nextSlot.starts_at, 'dd.'),
                        })}
                        duration={10000}/>
                )}
                <Alert
                    display={!!errorWhenBlockingSlot && !!triedSlot}
                    title={(
                        <>
                            {t('booking.slot-taken-1', {service: service ? service.name : ''})}
                            <b>&nbsp;{formatDate(triedSlot ? triedSlot.starts_at : new Date(), "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'")}</b> {t('booking.slot-taken-2')}
                        </>
                    )}/>
                {nextSlot && (
                    <Fragment>
                        {t('booking.next-free-slot')} <span
                        className={`inline-block text-sm border rounded px-3 py-1 cursor-pointer ${nextSlot === selectedSlot ? 'text-white bg-primary border-primary' : 'text-primary border-neutral-700'}`}
                        onClick={selectFirstSlot}>
                            {formatDate(nextSlot['starts_at'], "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'")}
                        </span>
                    </Fragment>
                )}
                <div
                    className="grid md:grid-cols-2 mt-5 w-full">
                    <Fragment>
                        <DayPicker onChange={changeDay} onNoAvailableDays={setDaysAvailable}
                                   daysLoading={setDaysLoading} nextSlot={nextSlot} selectedDay={selectedDay}/>
                        <TimeSlotPicker day={selectedDay} selectedSlot={selectedSlot} daysLoading={daysLoading}
                                        onChange={changeSlot} onChangeEarliestSlot={changeEarliestSlot}/>
                    </Fragment>
                </div>

                <SelectedHint service={service} selectedSlot={selectedSlot} garage={garage} organization={organization}/>
            </div>

            <div className="md:mt-10 row-span-6 relative">
                {service.show_checkpoint && garage.checkpoint && garage.checkpoint.name && (
                    <CheckpointDetails checkpoint={garage.checkpoint} garageType={garage.garageType}
                                       hideDescription={ServiceIsVehicleReturn(service)}/>
                )}
                {displayPrices && garageCalculation && (
                    <div className="mb-6">
                        <GarageListItemPrice priceIsFixed={service.fixed_price}
                                             calculation={garageCalculation}
                                             labour={garageCalculation?.labour_steps}
                                             parts={garageCalculation?.parts}
                                             service={service}/>
                    </div>
                )}
                {displayPremiumServices && (
                    <PremiumServiceSelector  organization={organization} service={service} garage={garage}/>
                )}
            </div>

            <button onClick={submit}
                    disabled={loadingWhenBlockingSlot || !daysAvailable || noSubServiceSelected() || !selectedSlot}
                    className="w-full bg-primary hover:bg-primary-100 px-8 py-2 mt-5 sm:w-72 text-white rounded text-center font-bold font-serif cursor-pointer no-underline disabled:opacity-50 disabled:cursor-not-allowed">
                {loadingWhenBlockingSlot && (
                    t('booking.blocking')
                )}
                {!loadingWhenBlockingSlot && (
                    service && ServiceIsVehicleReturn(service) ? t('booking.vehicleReturn.submit') : t('booking.submit')
                )}
            </button>

        </div>
    )
}
